import React, { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceEditTimesheetRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import NoteField from 'modules/inbox_tasks/components/NoteField'
import {
  getTimeWithoutTimezone,
  locationTypeText,
} from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/helpers'
import { AttendanceEditTimesheetRequestTaskContentTypeProps } from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/types'

const DeleteShiftRequestContent: FC<AttendanceEditTimesheetRequestTaskContentTypeProps> = ({
  id,
  requesterContent,
  moreDetails,
  workedTime,
  affecteeName,
}) => {
  const { data } = useFragment({
    fragment: AttendanceEditTimesheetRequestApprovableFragmentDoc,
    fragmentName: 'AttendanceEditTimesheetRequestApprovable',
    from: {
      __typename: 'AttendanceEditTimesheetRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const date = data.attendanceShiftDate
    ? stringToDate(data.attendanceShiftDate, 'backend_datetime')
    : undefined
  const displayDate = date ? i18nDateToString(date, 'ui_dmy_long') : ''

  const originalShift = data.attendanceShift
  const originalClockIn = originalShift?.clockIn
  const originalClockOut = originalShift?.clockOut
  const originalLocationType = originalShift?.locationType
  const originalObservations = originalShift?.observations
  const originalIsBreak = originalShift?.workable === false
  const originalBreakName = originalShift?.timeSettingsBreakConfiguration?.name

  const originalClockInTime = originalClockIn ? getTimeWithoutTimezone(originalClockIn) : undefined
  const originalClockOutTime = originalClockOut
    ? getTimeWithoutTimezone(originalClockOut)
    : undefined

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.date'),
      content: {
        text: displayDate,
        type: 'item' as const,
      },
    },
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.request_type'),
      content: {
        text: i18n.t(
          'tasks.approvals.attendance_edit_timesheet_request.fields.request_type_delete'
        ),
        type: 'item' as const,
      },
    },
    originalClockInTime && {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_in'),
      content: {
        text: originalClockInTime,
        type: 'item' as const,
      },
    },
    originalClockOutTime && {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_out'),
      content: {
        text: originalClockOutTime,
        type: 'item' as const,
      },
    },
    originalLocationType && {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.location_type'),
      content: {
        text: locationTypeText(originalLocationType),
        type: 'item' as const,
      },
    },
    originalObservations && {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.observations'),
      content: {
        text: originalObservations,
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  const summary = `${
    date && affecteeName
      ? originalIsBreak
        ? i18n.t('dashboard.todo.approvals.attendance.delete_break', {
            employee: affecteeName,
            date: i18nDateToString(date, 'ui_abbreviated_month_year'),
            clock_in: originalClockInTime ?? '',
            clock_out: originalClockOutTime ?? '',
            break_name: originalBreakName ?? '',
          })
        : i18n.t('dashboard.todo.approvals.attendance.delete_shift', {
            employee: affecteeName,
            date: i18nDateToString(date, 'ui_abbreviated_month_year'),
            clock_in: originalClockInTime ?? '',
            clock_out: originalClockOutTime ?? '',
          })
      : ''
  }${
    workedTime
      ? '\n' + i18n.t('dashboard.todo.approvals.attendance.worked_time', { workedTime }) + '.'
      : ''
  }`

  return (
    <Box width="full" gap="s16">
      <DetailsItemsList details={details} tableView />
      <Box paddingBottom="s4">
        <F0Link href={moreDetails.url}>{moreDetails.title}</F0Link>
      </Box>
      {!!data.reason && (
        <NoteField
          label={i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.reason')}
          content={data.reason}
        />
      )}
      {!!summary && <NoteField content={summary} />}
    </Box>
  )
}

export default DeleteShiftRequestContent
